import React, { useRef, useEffect } from "react";
import {
    Box,
    useTheme,
    alpha,
    Typography,
} from '@material-ui/core';

import { useMeasure } from "react-use";
import { useCalculator } from '../../state/calculatorState';
import { formatMoney } from '../../utils/utils'

const getPixelRatio = context => {
    let backingStore =
        context.backingStorePixelRatio ||
        context.webkitBackingStorePixelRatio ||
        context.mozBackingStorePixelRatio ||
        context.msBackingStorePixelRatio ||
        context.oBackingStorePixelRatio ||
        context.backingStorePixelRatio ||
        1;

    return (window.devicePixelRatio || 1) / backingStore;
};


export const InflationIncome = ({ data }) => {
    const ref = useRef();
    const theme = useTheme();
    // const [{ income: salary, salary: inputSalary },] = useCalculator();
    const [{ income: salary },] = useCalculator();

    const [refContainer, { width }] = useMeasure();

    useEffect(() => {
        let canvas = ref.current;
        let context = canvas.getContext('2d');

        // resize the canvas to the parent node
        canvas.style.width = '100%';
        canvas.style.height = '100%';

        let ratio = getPixelRatio(context);
        let w = getComputedStyle(canvas)
            .getPropertyValue('width')
            .slice(0, -2);
        let h = getComputedStyle(canvas)
            .getPropertyValue('height')
            .slice(0, -2);

        // reset the new values
        canvas.width = w * ratio;
        canvas.height = h * ratio;

        const value = salary
        draw(context, canvas, theme, ratio, value, data);
    });


    let height = width === 0 ? 0 : 280;
    const bgTint = alpha(theme.palette.background.overlay, 0.3)

    return (
        <Box style={{ borderRadius: 10 }}>
            <Box style={{ padding: 0, paddingBottom: 0, position: "relative", backgroundColor: bgTint }}>
                <Box display="flex" flexDirection="column" flexGrow={1} style={{ height: `${height}px`, width: '100%' }} ref={refContainer}>
                    <canvas ref={ref} width={'100%'} height={`${height}px`} />
                </Box>
                <Typography variant="body2" style={{ padding: 10 }}>
                    This forecast is using the most recent RBA CPI inflation forecast data and shoould be regarded as an estimate only.
                </Typography>
            </Box>
        </Box>
    );
}


// const linearInterpolate = (min, max, valueMin, valueMax, value) => {
//     const { m, c } = solveLinear(min, max, valueMin, valueMax)
//     return m * value + c
// }

const solveLinear = (min, max, valueMin, valueMax) => {
    const m = (max - min) / (valueMax - valueMin);
    const c = min - m * valueMin;
    return { m, c }
}


const draw = (ctx, canvas, theme, ratio, value, data) => {
    let w = canvas.width;
    let h = canvas.height
    const incomeData = { ...data };

    let margin = 30 * ratio;
    let textX = 0;
    let textY = 0;

    const fontSize = 12 * ratio;

    value = isFinite(value) ? value : 0;
    w = isFinite(w) ? w : 0;
    h = isFinite(h) ? h : 0;

    const bg = alpha(theme.palette.background.overlay, 0.3)

    ctx.clearRect(0, 0, w, h);

    ctx.fillStyle = `${bg}`;
    ctx.fillRect(0, 0, w, h);

    let min = incomeData.current;
    let max = incomeData.current;

    incomeData.projection.forEach(d => {
        max = Math.max(max, d.effective)
        min = Math.min(min, d.effective)
        min = Math.min(min, d.adjusted)
        max = Math.max(max, d.adjusted)
    })


    let range = max - min;
    if (range === 0) range = 1;
    min = min - range * 0.3;
    max = max + range * 0.3;

    const graphTop = margin
    const graphBottom = h - margin;
    const graphLeft = margin * 1
    const graphRight = w - 1.5*margin;
    // const graphCenter = h * 0.5 + margin;

    const { m: my, c: cy } = solveLinear(graphBottom, graphTop, min, max)
    const { m: mx, c: cx } = solveLinear(graphLeft, graphRight, 0, incomeData.projection ? incomeData.projection.length: 0)
    const graphCenter = my * incomeData.current + cy;

    ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.5);
    ctx.lineWidth = 1.0 * ratio;

    // Salary Forecast
    ctx.font = `${fontSize + 2}px ${theme.typography.h1.fontFamily[0]}`;
    ctx.fillStyle = theme.palette.primary.contrastText;
    ctx.textAlign = "left";
    textX = margin - 10 * ratio
    textY = graphTop - 10 * ratio
    ctx.fillText(`SALARY FORECAST`, textX, textY);

    // legend

    ctx.beginPath();
    ctx.fillStyle = theme.palette.primary.main;
    ctx.arc(margin - 10 * ratio, graphTop + 15 * ratio, 4, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillText(`Inflation Adjusted Salary`, margin, graphTop + 15 * ratio + fontSize * 0.5);


    ctx.beginPath();
    ctx.fillStyle = theme.palette.secondary.main
    ctx.arc(margin - 10 * ratio, graphTop + 30 * ratio, 4, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillText(`Effective Salary`, margin, graphTop + 30 * ratio + fontSize * 0.5);



    // axis
    ctx.beginPath();
    ctx.strokeStyle = theme.palette.primary.contrastText
    ctx.lineWidth = 1.0 * ratio;
    ctx.moveTo(margin - 10 * ratio, graphCenter)
    ctx.lineTo(graphRight, graphCenter)
    ctx.stroke()

    // current
    ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
    ctx.fillStyle = theme.palette.primary.contrastText;
    ctx.textAlign = "left";
    textX = margin - 10 * ratio
    textY = graphCenter - 25 * ratio
    // ctx.fillText(`Income:`, textX, textY);
    // textY = graphCenter - 10 * ratio
    // ctx.font = `${fontSize + 2 * ratio}px ${theme.typography.h1.fontFamily[0]}`;
    // ctx.fillText(`$${formatMoney(incomeData.current, 0)}`, textX, textY);

    ctx.beginPath();
    ctx.fillStyle = alpha(theme.palette.primary.contrastText, 0.8);
    ctx.arc(margin - 10 * ratio, graphCenter, 4, 0, 2 * Math.PI);
    ctx.fill();

    // start date
    // ctx.font = `${fontSize - 2 * ratio}px ${theme.typography.h1.fontFamily[0]}`;
    // ctx.fillStyle = theme.palette.primary.contrastText;
    // ctx.textAlign = "left";
    // ctx.fillText("Start date", textX, graphBottom + fontSize + 5);


    // x axis labels
    incomeData.projection.forEach((d, index) => {
        if (!d.skip) {
            const px = mx * (index ) + cx 
            const p_cx = mx * (index + 0)  + cx + 5 * ratio;
            ctx.font = `${fontSize + (0.1 * ratio)}px ${theme.typography.h1.fontFamily[0]}`;
            ctx.fillStyle = alpha(theme.palette.primary.contrastText, 0.5);
            ctx.textAlign = "left";
            ctx.fillText(d.date, p_cx, graphBottom + fontSize + 5);

            ctx.beginPath();
            ctx.moveTo(px, graphTop)
            ctx.lineTo(px, graphBottom + margin * 0.5)
            ctx.lineWidth = 1.0 * ratio;
            ctx.setLineDash([1 * ratio, 3 * ratio]);
            ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.2);
            ctx.stroke()
            ctx.setLineDash([]);
        }
    })


    // effective chart
    ctx.beginPath();
    ctx.moveTo(graphLeft, graphCenter)
    let forecast = false

    incomeData.projection.forEach((d, index) => {

        const px = mx * index+ 1 + cx;
        // const p_cx = mx * (index + 0.5) + cx;
        const p_cx = mx * (index ) + cx + 5 * ratio;
        const p_x = mx * (index + 1) + cx;
        const effective_y = my * d.effective + cy;

        if (!d.skip) {
            ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
            ctx.fillStyle = theme.palette.primary.contrastText;
            ctx.textAlign = "left";
            ctx.fillText(`$${formatMoney(d.effective, 0)}`, p_cx, effective_y + fontSize * 2);
        }
        ctx.lineTo(px, effective_y)
        ctx.lineTo(p_x, effective_y)

        if (forecast !== d.forecast) {
            forecast = d.forecast;
            // fill this section of the graph
            ctx.fillStyle = alpha(theme.palette.secondary.main, 0.4);
            ctx.lineTo(p_x, graphCenter)
            ctx.fill()
        }
    })

    ctx.strokeStyle = alpha(theme.palette.secondary.main, 0.8);
    ctx.lineWidth = 2 * ratio;
    ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.2);
    ctx.stroke();
    ctx.lineTo(graphRight, graphCenter)
    // ctx.closePath();
    ctx.fillStyle = alpha(theme.palette.secondary.main, 0.2);
    ctx.fill()

    // adjusted chart
    ctx.beginPath();
    ctx.moveTo(graphLeft, graphCenter)
    forecast = false

    incomeData.projection.forEach((d, index) => {

        const px = mx * index + cx;
        const p_cx = mx * (index) + cx + 5 * ratio;
        const p_x = mx * (index + 1) + cx;
        const adjusted_y = my * d.adjusted + cy;

        if (!d.skip) {
            ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
            ctx.fillStyle = theme.palette.primary.contrastText;
            ctx.textAlign = "left";
            ctx.fillText(`$${formatMoney(d.adjusted, 0)}`, p_cx, adjusted_y - fontSize * 1.5);
        }
        ctx.lineTo(px, adjusted_y)
        ctx.lineTo(p_x, adjusted_y)

        if (forecast !== d.forecast) {
            forecast = d.forecast;
            // fill this section of the graph
            ctx.fillStyle = alpha(theme.palette.primary.main, 0.4);
            ctx.lineTo(p_x, graphCenter)
            ctx.fill()
        }

    })
    ctx.strokeStyle = alpha(theme.palette.primary.main, 0.8);
    ctx.lineWidth = 2 * ratio;
    ctx.stroke();
    ctx.lineTo(graphRight, graphCenter)
    // ctx.closePath();
    ctx.fillStyle = alpha(theme.palette.primary.main, 0.2);
    ctx.fill()


}
